// src/components/Sidebar.jsx
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    LayoutGrid, Server, Activity, LogOut, Layers, Table, Database,
    ChevronLeft, ChevronRight, Loader2, CheckCircle2, Clock, XCircle, Mail
} from 'lucide-react';
import { PALETTE, THEME_CLASSES } from '@/lib/theme.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { CONFIG } from '@/lib/config.js';
import rapidLogo from '../../assets/rapidLogo.png';

export default function Sidebar({ isDark }) {
    // ---> FIXED: Pull 'user' from the context so we can read warehouse_tables <---
    const { tenantId, logout, fetchWithAuth, hasSectionAccess, user } = useAuth();
    const navigate = useNavigate();

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    const activeStyle = {
        backgroundColor: PALETTE.blue,
        color: PALETTE.white,
        boxShadow: `0 4px 10px -3px ${PALETTE.blue}60`
    };

    const { data, isLoading } = useQuery({
        queryKey: ['sidebarData', tenantId, user?.id],
        queryFn: async () => {
            if (!tenantId) return { recentActivity: [], counts: {} };

            const pollOptions = { headers: { 'X-Silent-Poll': 'true' } };

            const [logsRes, sourcesRes, dimensionsRes, rawTablesRes, pipelinesRes, warehouseRes, reportsRes, insightsRes] = await Promise.all([
                fetchWithAuth(`${CONFIG.API_BASE_URL}/data-connectors/logs?limit=5`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/data-connectors`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/dimensions`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/raw-data/tables`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/pipelines`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/warehouse/schema`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/tenant/reports`, pollOptions),
                fetchWithAuth(`${CONFIG.API_BASE_URL}/tenant/insights`, pollOptions)
            ]);

            let recentActivity = [];
            const counts = { sources: 0, dimensions: 0, rawTables: 0, pipelines: 0, warehouse: 0, reports: 0, dashboards: 0 };

            if (logsRes.ok) {
                const logsData = await logsRes.json();
                recentActivity = Array.isArray(logsData) ? logsData.map(item => ({
                    id: item.id,
                    name: item.connector_name || 'System Event',
                    status: item.status,
                    time: item.start_time
                })) : [];
            }

            const extractCount = async (res) => {
                if (!res.ok) return 0;
                try {
                    const data = await res.json();
                    if (Array.isArray(data)) return data.length;
                    if (data && typeof data === 'object') return Object.keys(data).length;
                    return 0;
                } catch {
                    return 0;
                }
            };

            counts.sources = await extractCount(sourcesRes);
            counts.dimensions = await extractCount(dimensionsRes);
            counts.rawTables = await extractCount(rawTablesRes);
            counts.reports = await extractCount(reportsRes);
            counts.dashboards = await extractCount(insightsRes);

            const allowedTables = user?.warehouse_tables || [];
            const isRestricted = user?.role !== 'admin' && user?.role !== 'superadmin';

            // ---> FIXED: Filter Pipeline count based on user RBAC <---
            if (pipelinesRes.ok) {
                try {
                    const pipeData = await pipelinesRes.json();
                    if (Array.isArray(pipeData)) {
                        counts.pipelines = isRestricted
                            ? pipeData.filter(p => allowedTables.includes(p.table_id)).length
                            : pipeData.length;
                    }
                } catch {
                    counts.pipelines = 0;
                }
            }

            // ---> FIXED: Filter Warehouse count based on user RBAC <---
            if (warehouseRes.ok) {
                try {
                    const whData = await warehouseRes.json();
                    if (Array.isArray(whData)) {
                        counts.warehouse = isRestricted
                            ? whData.filter(t => allowedTables.includes(t)).length
                            : whData.length;
                    }
                } catch {
                    counts.warehouse = 0;
                }
            }

            return { recentActivity, counts };
        },
        enabled: !!tenantId,
        refetchInterval: 10000 // HTTP Polling enabled
    });

    const recentActivity = data?.recentActivity || [];
    const counts = data?.counts || { sources: 0, dimensions: 0, rawTables: 0, pipelines: 0, warehouse: 0, reports: 0, dashboards: 0 };

    const menuItems = [
        { path: '/', label: 'Overview', icon: LayoutGrid, section: null, tourClass: '' },
        { path: '/data-connectors', label: 'Connectors', icon: Server, count: counts.sources, section: 'data-connectors', tourClass: 'tour-nav-sources' },
        { path: '/raw-tables', label: 'Raw Tables', icon: Table, count: counts.rawTables, section: 'raw-tables', tourClass: 'tour-nav-raw' },
        { path: '/dimensions', label: 'Dimensions', icon: Layers, count: counts.dimensions, section: 'dimensions', tourClass: 'tour-nav-dims' },
        { path: '/pipelines', label: 'Pipelines', icon: Activity, count: counts.pipelines, section: 'pipelines', tourClass: 'tour-nav-pipes' },
        { path: '/warehouse', label: 'Warehouse', icon: Database, count: counts.warehouse, section: 'warehouse', tourClass: 'tour-nav-warehouse' },
        { path: '/reports', label: 'Reports', icon: Mail, count: counts.reports, section: 'reports', tourClass: 'tour-nav-reports' },
        { path: '/insights', label: 'Insights', icon: LayoutGrid, count: counts.dashboards, section: 'insights', tourClass: 'tour-nav-insights' },
    ];

    const authorizedMenuItems = menuItems.filter(item => {
        if (!item.section) return true;
        return hasSectionAccess(item.section);
    });

    const handleLogoutClick = (e) => {
        e.preventDefault();
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <>
            <aside
                className={`
                    backdrop-blur-xl border-r shrink-0 hidden md:flex flex-col transition-all duration-300 relative z-[70] overflow-visible
                    ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)}
                    ${isCollapsed ? 'w-16' : 'w-48'}
                `}
            >
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className={`absolute -right-3 top-9 w-6 h-6 rounded-full border flex items-center justify-center z-[80] transition-colors shadow-sm
                        ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} ${THEME_CLASSES.textPrimary(isDark)} ${THEME_CLASSES.bgHover(isDark)}
                    `}
                >
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </button>

                <div className={`h-12 px-4 flex items-center w-full shrink-0 ${isCollapsed ? 'justify-center' : 'space-x-3'}`}>
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center transition-transform hover:scale-105">
                        <img src={rapidLogo} alt="Rapid Logo" className="w-full h-full object-contain" />
                    </div>
                    {!isCollapsed && (
                        <div className="overflow-hidden whitespace-nowrap">
                            <span className={`font-bold tracking-tight text-sm block leading-none ${THEME_CLASSES.textPrimary(isDark)}`}>Rapid Jaguar</span>
                            <span className={`text-[9px] font-bold uppercase tracking-widest ${THEME_CLASSES.textSecondary(isDark)}`}>Data Warehouse</span>
                        </div>
                    )}
                </div>

                <nav className="flex-1 px-3 space-y-1 overflow-hidden hover:overflow-y-auto scrollbar-hide py-3">
                    {authorizedMenuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                state={{ resetView: Date.now() }}
                                className={({ isActive }) => `
                                    ${item.tourClass} w-full flex items-center rounded-xl transition-all duration-200 group font-medium text-[13px] relative
                                    ${isCollapsed ? 'justify-center px-0 py-2.5' : 'space-x-3 px-3 py-2.5'}
                                    ${!isActive ? `${THEME_CLASSES.bgHover(isDark)}${THEME_CLASSES.textSecondary(isDark)}` : ''}
                                `}
                                style={({ isActive }) => isActive ? activeStyle : {}}
                                title={isCollapsed ? item.label : ''}
                            >
                                {({ isActive }) => (
                                    <>
                                        <Icon size={18} className={`shrink-0 ${isActive ? '' : 'opacity-70 group-hover:opacity-100'}`} />
                                        {!isCollapsed && <span>{item.label}</span>}
                                        {item.count > 0 && (
                                            <span
                                                className={`${isCollapsed
                                                    ? 'absolute top-1.5 right-1.5 w-2 h-2 rounded-full border border-black'
                                                    : `ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold transition-colors ${
                                                        isActive
                                                            ? 'bg-white/20 text-white'
                                                            : `${THEME_CLASSES.bgHighlight(isDark)} ${THEME_CLASSES.textSecondary(isDark)}`
                                                    }`
                                                }`}
                                                style={isCollapsed ? { backgroundColor: PALETTE.red || '#ef4444' } : {}}
                                            >
                                                {!isCollapsed && item.count}
                                            </span>
                                        )}
                                    </>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className={`p-3 shrink-0 border-t ${THEME_CLASSES.borderSubtle(isDark)}`}>
                    {!isCollapsed && (
                        <div className="mb-3 px-1">
                            <h4 className={`text-[10px] font-bold uppercase tracking-widest mb-2 opacity-50 ${THEME_CLASSES.textSecondary(isDark)}`}>Recent Activity</h4>
                            {isLoading ? (
                                <div className="flex justify-center py-2"><Loader2 className="animate-spin text-gray-400" size={14} /></div>
                            ) : recentActivity.length > 0 ? (
                                <div className="space-y-1.5">
                                    {recentActivity.map((log, i) => {
                                        const isSuccess = ['SUCCESS', 'COMPLETED', 'ACTIVE'].includes(log.status?.toUpperCase());
                                        const StatusIcon = isSuccess ? CheckCircle2 : (log.status?.toUpperCase() === 'FAILED' || log.status?.toUpperCase() === 'ERROR' ? XCircle : Clock);
                                        const colorClass = isSuccess ? THEME_CLASSES.textGreen(isDark) : (log.status?.toUpperCase() === 'FAILED' || log.status?.toUpperCase() === 'ERROR' ? 'text-red-500' : 'text-blue-500');

                                        return (
                                            <div key={i} className="flex items-center space-x-2 text-xs truncate" title={`${log.name} - ${log.status}`}>
                                                <StatusIcon size={12} className={`shrink-0 ${colorClass}`} />
                                                <span className={`truncate font-medium ${THEME_CLASSES.textSecondary(isDark)}`}>{log.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className={`text-[10px] italic ${THEME_CLASSES.textSecondary(isDark)}`}>No recent activity</p>
                            )}
                        </div>
                    )}
                </div>
            </aside>

            {isLogoutModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200 p-4">
                    <div className={`w-full max-w-sm p-6 rounded-2xl shadow-2xl border ${THEME_CLASSES.bgCard(isDark)} ${THEME_CLASSES.borderSubtle(isDark)} animate-in zoom-in-95 duration-200`}>
                        <h3 className={`text-lg font-bold mb-2 ${THEME_CLASSES.textPrimary(isDark)}`}>Sign Out</h3>
                        <p className={`text-sm mb-6 ${THEME_CLASSES.textSecondary(isDark)}`}>
                            Are you sure you want to sign out of your account?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setIsLogoutModalOpen(false)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${THEME_CLASSES.bgHover(isDark)} ${THEME_CLASSES.textPrimary(isDark)}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmLogout}
                                className="px-4 py-2 rounded-xl text-sm font-medium text-white hover:opacity-80 transition-opacity shadow-sm"
                                style={{ backgroundColor: PALETTE.red || '#ef4444' }}
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}